import { bN as __rest, c as classNames, a8 as Link, u as useStrings, U as FontAwesomeIcon, ai as createClassName, d as useContext, a7 as useVinResolver, f as useTypedSelector, s as selectLocale } from './className-0524835a.js';
import React__default, { forwardRef, useRef, useState, useId, useMemo, Fragment } from 'react';
import { b as useDateText, T as Tooltip, D as DateText } from './DateText-ac378e62.js';
import { f as faChevronRightExports } from './NotFoundScreen-faa3d07d.js';
import { c as MenuItem, b as Menu } from './MenuItem-0e2b904d.js';
import { D as DefinitionList } from './DefinitionList-22f6ed54.js';
import { w as useModuleVehicleInformation } from './DocumentScreenSkeleton-96f9a1fb.js';

/** A link with the appearance of a button. */
const ButtonLink = forwardRef((props, ref) => {
    const { ariaLabel, className, variant } = props, linkProps = __rest(props, ["ariaLabel", "className", "variant"]);
    const classes = classNames('ButtonLink', className, {
        [`ButtonLink-${variant}`]: variant && variant !== 'default'
    });
    return React__default.createElement(Link, Object.assign({}, linkProps, { ref: ref, className: classes, ariaLabel: ariaLabel })); // eslint-disable-line react/jsx-props-no-spreading
});
ButtonLink.displayName = 'ButtonLink';

const DocumentLabel = ({ document, label }) => {
    const { document: strings } = useStrings();
    const classes = classNames('DocumentLabel', `DocumentLabel-${label.id}`);
    const embargoDate = useDateText(document.embargoDate);
    const title = label.id === 'embargoed' && embargoDate ? React__default.createElement(React__default.Fragment, null, strings.embargoedUntilDate(embargoDate)) : '';
    return React__default.createElement(Tooltip, { title: title },
        React__default.createElement("span", { className: classes }, label.name));
};

const DocumentLabels = ({ document }) => {
    if (document.labels.length === 0) {
        return null;
    }
    return React__default.createElement("span", { className: "DocumentLabels" }, document.labels.map(label => React__default.createElement(DocumentLabel, { key: label.id, document: document, label: label })));
};

const ButtonBarMenuItem = ({ title, icon, selected, disabled, hasSubmenu, onClick }) => {
    return React__default.createElement(MenuItem, { selected: selected, disabled: disabled, onClick: onClick },
        React__default.createElement("div", { className: className$2() },
            React__default.createElement(FontAwesomeIcon, { icon: icon, size: "1x", className: className$2('icon') }),
            React__default.createElement("div", { className: className$2('title') }, title),
            hasSubmenu && React__default.createElement(FontAwesomeIcon, { icon: faChevronRightExports.faChevronRight, size: "sm", className: className$2('chevron') })));
};
const className$2 = createClassName('ButtonBarMenuItem');

const ButtonBarItemButton = ({ subbuttons, onClick, title, disabled, icon, size }) => {
    const itemButtonRef = useRef(null);
    const [isItemButtonMenuOpen, setIsItemButtonMenuOpen] = useState(false);
    const hasSubbuttons = subbuttons && subbuttons.length > 0;
    const id = useId();
    function onItemButtonMenuClick() {
        if (hasSubbuttons) {
            setIsItemButtonMenuOpen(true);
            return;
        }
        onClick();
    }
    function onItemButtonMenuClose() {
        setIsItemButtonMenuOpen(false);
    }
    function onSubButtonClick(button) {
        setIsItemButtonMenuOpen(false);
        button.onClick();
    }
    return React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Tooltip, { title: title, arrow: true },
            React__default.createElement("div", { id: id },
                React__default.createElement("button", { "aria-labelledby": id, disabled: disabled, onClick: onItemButtonMenuClick, ref: itemButtonRef },
                    React__default.createElement(FontAwesomeIcon, { icon: icon, size: size === 'large' ? 'lg' : '1x' })))),
        hasSubbuttons &&
            React__default.createElement(Menu, { open: isItemButtonMenuOpen, onClose: onItemButtonMenuClose, anchorEl: itemButtonRef.current, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }, subbuttons === null || subbuttons === void 0 ? void 0 : subbuttons.map(subbutton => (React__default.createElement(ButtonBarMenuItem, { key: subbutton.id, title: subbutton.title, icon: subbutton.icon, disabled: subbutton.disabled, onClick: () => onSubButtonClick(subbutton) })))));
};

var faEllipsisV = {};

var hasRequiredFaEllipsisV;

function requireFaEllipsisV () {
	if (hasRequiredFaEllipsisV) return faEllipsisV;
	hasRequiredFaEllipsisV = 1;
	(function (exports) {
		Object.defineProperty(exports, '__esModule', { value: true });
		var prefix = 'fas';
		var iconName = 'ellipsis-v';
		var width = 192;
		var height = 512;
		var ligatures = [];
		var unicode = 'f142';
		var svgPathData = 'M96 184c39.8 0 72 32.2 72 72s-32.2 72-72 72-72-32.2-72-72 32.2-72 72-72zM24 80c0 39.8 32.2 72 72 72s72-32.2 72-72S135.8 8 96 8 24 40.2 24 80zm0 352c0 39.8 32.2 72 72 72s72-32.2 72-72-32.2-72-72-72-72 32.2-72 72z';

		exports.definition = {
		  prefix: prefix,
		  iconName: iconName,
		  icon: [
		    width,
		    height,
		    ligatures,
		    unicode,
		    svgPathData
		  ]};

		exports.faEllipsisV = exports.definition;
		exports.prefix = prefix;
		exports.iconName = iconName;
		exports.width = width;
		exports.height = height;
		exports.ligatures = ligatures;
		exports.unicode = unicode;
		exports.svgPathData = svgPathData;
} (faEllipsisV));
	return faEllipsisV;
}

var faEllipsisVExports = requireFaEllipsisV();

const ButtonBarOverflowButtons = ({ buttons }) => {
    var _a;
    const { buttonBar: strings } = useStrings();
    const menuAnchor = useRef(null);
    const [menuVisible, setMenuVisible] = useState(false);
    const [submenuParent, setSubmenuParent] = useState(null);
    const [submenuItems, setSubmenuItems] = useState([]);
    function onMenuItemClick(event, button) {
        var _a;
        if ((_a = button.subbuttons) === null || _a === void 0 ? void 0 : _a.length) {
            setSubmenuParent({
                id: button.id,
                ref: event.currentTarget
            });
            setSubmenuItems(button.subbuttons);
        }
        else {
            setSubmenuParent(null);
            setMenuVisible(false);
            button.onClick();
        }
    }
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Tooltip, { title: strings.overflowButtonTitle, arrow: true },
            React__default.createElement("button", { ref: menuAnchor, className: "ButtonBar-menuButton", onClick: () => setMenuVisible(!menuVisible) },
                React__default.createElement(FontAwesomeIcon, { icon: faEllipsisVExports.faEllipsisV, size: "lg" }))),
        React__default.createElement(Menu, { open: menuVisible, onClose: () => setMenuVisible(false), anchorEl: menuAnchor.current, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }, buttons.map(button => (React__default.createElement(ButtonBarMenuItem, { key: button.id, title: button.title, icon: button.icon, selected: button.id === (submenuParent === null || submenuParent === void 0 ? void 0 : submenuParent.id), disabled: button.disabled, hasSubmenu: button.subbuttons && button.subbuttons.length > 0, onClick: event => onMenuItemClick(event, button) })))),
        React__default.createElement(Menu, { open: submenuParent !== null, onClose: () => setSubmenuParent(null), anchorEl: (_a = submenuParent === null || submenuParent === void 0 ? void 0 : submenuParent.ref) !== null && _a !== void 0 ? _a : null, anchorOrigin: { vertical: 'top', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }, submenuItems.map(item => (React__default.createElement(ButtonBarMenuItem, { key: item.id, title: item.title, icon: item.icon, disabled: item.disabled, onClick: event => onMenuItemClick(event, item) }))))));
};

const ButtonBar = ({ buttons, size = 'large' }) => {
    const visibleButtons = buttons.filter(button => button.visible === undefined || button.visible === 'visible');
    const overflowButtons = buttons.filter(button => button.visible === 'inOverflow');
    const classes = classNames(className$1(), {
        [className$1('small')]: size === 'small'
    });
    return React__default.createElement("div", { className: classes },
        visibleButtons.map(button => {
            // eslint-disable-next-line react/jsx-props-no-spreading
            return React__default.createElement(ButtonBarItemButton, Object.assign({ key: button.id }, button, { size: size }));
        }),
        overflowButtons.length > 0 && React__default.createElement(ButtonBarOverflowButtons, { buttons: overflowButtons }));
};
const className$1 = createClassName('ButtonBar');

var faDownload = {};

var hasRequiredFaDownload;

function requireFaDownload () {
	if (hasRequiredFaDownload) return faDownload;
	hasRequiredFaDownload = 1;
	(function (exports) {
		Object.defineProperty(exports, '__esModule', { value: true });
		var prefix = 'fas';
		var iconName = 'download';
		var width = 512;
		var height = 512;
		var ligatures = [];
		var unicode = 'f019';
		var svgPathData = 'M216 0h80c13.3 0 24 10.7 24 24v168h87.7c17.8 0 26.7 21.5 14.1 34.1L269.7 378.3c-7.5 7.5-19.8 7.5-27.3 0L90.1 226.1c-12.6-12.6-3.7-34.1 14.1-34.1H192V24c0-13.3 10.7-24 24-24zm296 376v112c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24V376c0-13.3 10.7-24 24-24h146.7l49 49c20.1 20.1 52.5 20.1 72.6 0l49-49H488c13.3 0 24 10.7 24 24zm-124 88c0-11-9-20-20-20s-20 9-20 20 9 20 20 20 20-9 20-20zm64 0c0-11-9-20-20-20s-20 9-20 20 9 20 20 20 20-9 20-20z';

		exports.definition = {
		  prefix: prefix,
		  iconName: iconName,
		  icon: [
		    width,
		    height,
		    ligatures,
		    unicode,
		    svgPathData
		  ]};

		exports.faDownload = exports.definition;
		exports.prefix = prefix;
		exports.iconName = iconName;
		exports.width = width;
		exports.height = height;
		exports.ligatures = ligatures;
		exports.unicode = unicode;
		exports.svgPathData = svgPathData;
} (faDownload));
	return faDownload;
}

var faDownloadExports = requireFaDownload();

function toVal(mix) {
	var k, y, str='';

	if (typeof mix === 'string' || typeof mix === 'number') {
		str += mix;
	} else if (typeof mix === 'object') {
		if (Array.isArray(mix)) {
			for (k=0; k < mix.length; k++) {
				if (mix[k]) {
					if (y = toVal(mix[k])) {
						str && (str += ' ');
						str += y;
					}
				}
			}
		} else {
			for (k in mix) {
				if (mix[k]) {
					str && (str += ' ');
					str += k;
				}
			}
		}
	}

	return str;
}

function clsx () {
	var i=0, tmp, x, str='';
	while (i < arguments.length) {
		if (tmp = arguments[i++]) {
			if (x = toVal(tmp)) {
				str && (str += ' ');
				str += x;
			}
		}
	}
	return str;
}

var clsx_m = /*#__PURE__*/Object.freeze({
    __proto__: null,
    default: clsx
});

const VehicleInformationImage = ({ imageUrl }) => {
    const { vehicle: strings } = useStrings();
    return (React__default.createElement("div", { className: "VehicleInformationImage-image" }, imageUrl ? React__default.createElement("figure", { "aria-labelledby": "VehicleInformationImage-imageCaption" },
        React__default.createElement("img", { src: imageUrl, alt: strings.imageAlt }),
        React__default.createElement("figcaption", { id: "VehicleInformationImage-imageCaption" }, strings.imageCaption)) : React__default.createElement("div", { className: "VehicleInformationImage-noImage" }, strings.noImage)));
};

const VehicleInformationDefinitionList = ({ vehicle }) => {
    const { vehicle: strings } = useStrings();
    const { showVehicleMetadata } = useContext();
    const vinResolver = useVinResolver();
    const vehicleDateResolver = (dateString) => {
        return dateString ? React__default.createElement(DateText, { date: dateString, format: DateText.DATE_LONG }) : undefined;
    };
    const metadataItems = useMemo(() => showVehicleMetadata && vehicle.metadata ?
        Object.entries(vehicle.metadata).map(([name, value]) => ({ name, value })) : [], [showVehicleMetadata, vehicle.metadata]);
    const items = useModuleVehicleInformation(vehicle, [
        { name: strings.vinLabel, value: vehicle.ref === 'vin' ? vinResolver(vehicle.vin) : undefined, copyToClipboard: true },
        { name: strings.modelYearLabel, value: vehicle.year ? String(vehicle.year) : undefined },
        { name: strings.modelNameLabel, value: vehicle.name },
        { name: strings.modelCodeLabel, value: vehicle.code },
        { name: strings.modelEngineSerialNumberLabel, value: vehicle === null || vehicle === void 0 ? void 0 : vehicle.engineSerialNumber },
        { name: strings.modelSellerDealerCodeLabel, value: vehicle === null || vehicle === void 0 ? void 0 : vehicle.sellerDealerCode },
        { name: strings.modelBuildDateLabel, value: vehicleDateResolver(vehicle.buildDate) },
        { name: strings.modelSaleDateLabel, value: vehicleDateResolver(vehicle.saleDate) },
        { name: strings.modelWarrantyStartDateLabel, value: vehicleDateResolver(vehicle.warrantyStartDate) },
        { name: strings.modelWarrantyEndDateLabel, value: vehicleDateResolver(vehicle.warrantyEndDate) },
        ...metadataItems
    ]);
    return React__default.createElement(DefinitionList, { skipMissingValues: true, definitions: items });
};

function groupFeaturesByFamilyCode(features) {
    return features.reduce((prev, feature) => {
        const id = feature.familyCode;
        return Object.assign(Object.assign({}, prev), { [id]: prev[id] ? [...prev[id], feature] : [feature] });
    }, {});
}
const FeaturesTable = ({ features }) => {
    const { vehicle: strings } = useStrings();
    return (React__default.createElement("div", { className: className() },
        React__default.createElement("dl", { role: "list", "aria-label": strings.featuresTitle }, Object.entries(groupFeaturesByFamilyCode(features)).map(([familyCode, features]) => {
            const id = className(`feature${familyCode}`);
            const { description: singleFeatureDescription, familyDescription } = features[0];
            return (React__default.createElement(Fragment, { key: familyCode }, features.length === 1 ? (React__default.createElement(Fragment, { key: familyCode },
                React__default.createElement("dt", { id: id }, familyDescription),
                React__default.createElement("dd", { "aria-labelledby": id },
                    singleFeatureDescription,
                    " ",
                    features[0].code !== singleFeatureDescription ? `(${features[0].code})` : ''))) : (React__default.createElement(Fragment, { key: familyCode },
                React__default.createElement("dt", { id: familyCode }, `${familyDescription} (${features.length})`),
                React__default.createElement("dd", { "aria-labelledby": familyCode }),
                features.map(({ code, description }) => (React__default.createElement(Fragment, { key: code },
                    React__default.createElement("dt", { id: code, className: className('nestedDesc') }, code),
                    React__default.createElement("dd", { className: className('nestedDesc'), "aria-labelledby": code }, description))))))));
        }))));
};
const className = createClassName('FeaturesTable');

/* Only English locale (one/other) is supported at present. */
function getPluralString(string, count, _locale) {
    return count === 1 ? string.one : string.other(count);
}
function usePluralStrings() {
    useTypedSelector(selectLocale);
    return {
        getPluralString: (string, count) => {
            return getPluralString(string, count);
        }
    };
}

export { ButtonLink as B, DocumentLabels as D, FeaturesTable as F, VehicleInformationImage as V, DocumentLabel as a, ButtonBar as b, VehicleInformationDefinitionList as c, clsx_m as d, clsx as e, faDownloadExports as f, usePluralStrings as u };
